import { Navigate, Outlet } from 'react-router-dom';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { usePermissions } from '@/context/PermissionContext';
import Loader from '@/components/ui/loader';
import { RootState } from '@/store';

type AccessType = 'read' | 'create' | 'update' | 'delete' | 'detailedView';

type ModuleRouteProps = {
  moduleName: string;
  children?: ReactNode;
  requiredAccess?: AccessType | AccessType[];
};

const getRoutePriority = (userRoleId: number) => [
  { permissionKey: 'Dashboard', href: '/dashboard' },
  { permissionKey: 'Users', href: `/members/${userRoleId}` },
  { permissionKey: 'SportAnalysis', href: '/sport-analysis' },
  { permissionKey: 'MyReport', href: '/bet-list' },
  { permissionKey: 'EventManagement', href: '/event-management' },
  { permissionKey: 'BetControl', href: '/bet-control' },
  { permissionKey: 'CasinoControl', href: '/casino-management' },
  { permissionKey: 'Transaction', href: '/requests' },
  { permissionKey: 'ContentManagement', href: '/content-management' },
  { permissionKey: 'Bonus', href: '/bonus/policy-management' },
  { permissionKey: 'BankingManagement', href: '/requests' },
  { permissionKey: 'Roles Privileges', href: '/roles-and-privileges' },
  { permissionKey: 'Commision', href: '/match-odds-commission-setting' },
  { permissionKey: 'Account Statement', href: '/rolewise-account-statement' },
  { permissionKey: 'Export History', href: '/export' },
  { permissionKey: 'Change Password', href: '/settings' },
  { permissionKey: 'General Settings', href: '/general-settings' },
  { permissionKey: 'Activity Logs', href: '/activity-logs' },
];

const ModuleRoute = ({ moduleName, children, requiredAccess = 'read' }: ModuleRouteProps) => {
  const { hasModuleAccess, isOwner, user, isDownlineUser } = usePermissions();
  const roles = useSelector((state: RootState) => state.roles);
  const userRoleId = roles.availableRoles.find((r) => r.name.toLowerCase() === 'user')?.id;

  const isPrivilegedUser = isOwner || isDownlineUser;

  if ((!user && !isPrivilegedUser) || !userRoleId) {
    return <Loader />;
  }
  const accessList = Array.isArray(requiredAccess) ? requiredAccess : [requiredAccess];

  const hasRequiredAccess =
    isPrivilegedUser || accessList.every((access) => hasModuleAccess(moduleName, access));

  if (!hasRequiredAccess) {
    if (moduleName === 'Dashboard') {
      const firstAccessibleRoute = getRoutePriority(userRoleId).find(
        (route) => isPrivilegedUser || hasModuleAccess(route.permissionKey, 'read')
      );

      return <Navigate to={firstAccessibleRoute?.href || '/settings'} replace />;
    }

    return <Navigate to="/settings" replace />;
  }

  if (children) {
    return <>{children}</>;
  }

  return <Outlet />;
};

export default ModuleRoute;
